import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

const Container = styled.div`
  position: relative;
  width: 100%;
  font-family: Arial, sans-serif;
  margin: 10px;
`;

const Label = styled.label`
  color: #333333;
  margin-right: 10px;
  font-size: 12px;
  font-weight: 600;
  font-style: normal;
  line-height: 1.75;
  font-stretch: normal;
  letter-spacing: normal;
`;

const InputTag = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  border: solid 1px #888888;
  border-radius: 4px;
  cursor: text;
  min-height: 34px;
  background: #ffffff;
  transition: all 0.3s ease-in-out;
  overflow-y: auto;
  max-height: 124px;

  &:focus-within {
    border: 1px solid #848484;
  }
`;

const Tag = styled.span`
  background: #f4f4f4;
  color: #333333;
  padding: 6px 12px;
  margin: 4px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  font-size: 12px;
  height: 22px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #333333;
  margin-left: 6px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;

  &:hover {
    color: #4cb358;
  }
`;

const Input = styled.input`
  width: 100%;
  border: none;
  outline: none;
  flex-grow: 1;
  padding: 6px;
  min-width: 50px;
  font-size: 14px;
  background: transparent;
`;

const Dropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  border: 1px solid #d1d1d1;
  border-radius: 10px;
  background: white;
  list-style: none;
  margin: 4px 0 0 0;
  padding: 0;
  max-height: 180px;
  overflow-y: auto;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  display: ${({ show }) => (show ? "block" : "none")};
  z-index: 1000;
`;

const Option = styled.li`
  padding: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: #e3e8f0;
  }
`;

const InputBox = ({ label, ActiveDropdown, onChange }) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      addOption();
    }
  };

  const addOption = () => {
    const trimmedValues = inputValue
      .split(/[ ,;]+/) 
      .map((value) => value.trim())
      .filter((value) => value !== "" && !options.includes(value)); 

    if (trimmedValues.length > 0) {
      const updatedOptions = [...options, ...trimmedValues];
      setOptions(updatedOptions);
      if (onChange) {
        onChange(updatedOptions);
      }
    }
    setInputValue("");
  };

  const removeOption = (optionToRemove) => {
    const updatedOptions = options.filter((option) => option !== optionToRemove);
    setOptions(updatedOptions);
    if (onChange) {
      onChange(updatedOptions);
    }
  };

  const handleSelect = (value) => {
    setInputValue(value);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (ActiveDropdown === false) {
      setShowDropdown(false);
    }
  }, [ActiveDropdown]);

  return (
    <Container ref={containerRef}>
      {label && <Label>{label}</Label>}
      <InputTag onClick={() => inputRef.current.focus()}>
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addOption} // Agregar cuando pierda el foco
          placeholder="Escribe y presiona Enter..."
        />
        {options.map((option, index) => (
          <Tag key={index}>
            {option}
            <RemoveButton onClick={() => removeOption(option)}>×</RemoveButton>
          </Tag>
        ))}
      </InputTag>
      <Dropdown show={showDropdown && ActiveDropdown}>
        {options.map((option, index) => (
          <Option key={index} onClick={() => handleSelect(option)}>
            {option}
          </Option>
        ))}
      </Dropdown>
    </Container>
  );
};

export default InputBox;